import { GuidService } from 'ui-components/services/guid-service';
import {
  OPERATOR_TYPE_VALUE_EQUALS_TRUE,
  OPERATOR_TYPE_VALUE_EQUALS_FALSE,
} from '../../../../../components/filter-editor/constants/filter-operator-type';
import { WORKFLOW_FILTER_TYPE } from '../../../api/automation-workflow-types';
import type { AppliedFilters } from '../../../api/automation-workflow-types';
import type { WorkflowFilterDraft } from '../../types.d';
import type { FilterRegistry } from '../../registries/registry-types.d';
import {
  STRING_OPERATOR_MAP,
  NUMERIC_OPERATOR_MAP,
  DATETIME_OPERATOR_MAP,
  COLLECTION_OPERATOR_MAP,
  STRING_OPERATOR_REVERSE,
  NUMERIC_OPERATOR_REVERSE,
  DATETIME_OPERATOR_REVERSE,
  COLLECTION_OPERATOR_REVERSE,
} from './operator-maps';

type FilterTypeHandler = {
  fromApi: (definition: AppliedFilters) => WorkflowFilterDraft[];
  toApi: (drafts: WorkflowFilterDraft[], registry: FilterRegistry) => AppliedFilters | null;
};

const filterCriteriaHandler: FilterTypeHandler = {
  fromApi: (definition) => {
    const { stringFilters = [], numericFilters = [], dateTimeFilters = [], collectionFilters = [], booleanFilters = [] } = definition;

    return [
      ...stringFilters.map(
        (f): WorkflowFilterDraft => ({
          id: GuidService.newGuid(),
          fieldName: f.field,
          operator: STRING_OPERATOR_REVERSE[f.operator] ?? f.operator,
          value: f.value,
        }),
      ),
      ...numericFilters.map(
        (f): WorkflowFilterDraft => ({
          id: GuidService.newGuid(),
          fieldName: f.field,
          operator: NUMERIC_OPERATOR_REVERSE[f.operator] ?? f.operator,
          value: f.value,
        }),
      ),
      ...dateTimeFilters.map(
        (f): WorkflowFilterDraft => ({
          id: GuidService.newGuid(),
          fieldName: f.field,
          operator: DATETIME_OPERATOR_REVERSE[f.operator] ?? f.operator,
          value: f.value,
        }),
      ),
      ...collectionFilters.map(
        (f): WorkflowFilterDraft => ({
          id: GuidService.newGuid(),
          fieldName: f.field,
          operator: COLLECTION_OPERATOR_REVERSE[f.operator] ?? f.operator,
          value: f.values,
        }),
      ),
      ...booleanFilters.map(
        (f): WorkflowFilterDraft => ({
          id: GuidService.newGuid(),
          fieldName: f.field,
          operator: f.value ? OPERATOR_TYPE_VALUE_EQUALS_TRUE : OPERATOR_TYPE_VALUE_EQUALS_FALSE,
          value: '',
        }),
      ),
    ];
  },

  toApi: (drafts, registry) => {
    if (drafts.length === 0) return null;

    const stringFilters: Array<{ field: string; operator: string; value: string }> = [];
    const numericFilters: Array<{ field: string; operator: string; value: string }> = [];
    const dateTimeFilters: Array<{ field: string; operator: string; value: string }> = [];
    const collectionFilters: Array<{ field: string; operator: string; values: string[] }> = [];
    const booleanFilters: Array<{ field: string; value: boolean }> = [];

    for (const draft of drafts) {
      const entry = registry[draft.fieldName];
      if (!entry) continue;

      switch (entry.filterKind) {
        case 'string':
          stringFilters.push({
            field: draft.fieldName,
            operator: STRING_OPERATOR_MAP[draft.operator] ?? draft.operator,
            value: draft.value as string,
          });
          break;
        case 'numeric':
          numericFilters.push({
            field: draft.fieldName,
            operator: NUMERIC_OPERATOR_MAP[draft.operator] ?? draft.operator,
            value: draft.value as string,
          });
          break;
        case 'datetime':
          dateTimeFilters.push({
            field: draft.fieldName,
            operator: DATETIME_OPERATOR_MAP[draft.operator] ?? draft.operator,
            value: draft.value as string,
          });
          break;
        case 'collection':
          collectionFilters.push({
            field: draft.fieldName,
            operator: COLLECTION_OPERATOR_MAP[draft.operator] ?? draft.operator,
            values: draft.value as string[],
          });
          break;
        case 'boolean':
          booleanFilters.push({
            field: draft.fieldName,
            value: draft.operator === OPERATOR_TYPE_VALUE_EQUALS_TRUE,
          });
          break;
      }
    }

    return {
      ...(stringFilters.length ? { stringFilters } : {}),
      ...(numericFilters.length ? { numericFilters } : {}),
      ...(dateTimeFilters.length ? { dateTimeFilters } : {}),
      ...(collectionFilters.length ? { collectionFilters } : {}),
      ...(booleanFilters.length ? { booleanFilters } : {}),
    };
  },
};

// Dispatch map — add new filter types here when the backend introduces them
export const filterTypeHandlers: Partial<Record<string, FilterTypeHandler>> = {
  [WORKFLOW_FILTER_TYPE.filterCriteria]: filterCriteriaHandler,
};
